import { restoreSnapshotTokenCollection } from '../utils/battleRoom/BattleRoomSnapshotRehydrator';
import { serializeBattleRoomTokenCollection } from '../utils/battleRoom/BattleRoomSnapshotSerializer';
// date_of_change: 2026-05-19 - GAP-001: Needed so restoreCharacterDataFromReferences can pass real
// canonical entity maps to the rehydrator (previously passed {}, defeating canonical fallback).
import UnifiedDataManager from './UnifiedDataManager';
import {
  SESSION_INDEX_STORAGE_KEY,
  getTierConfig,
  isSessionExpired
} from '../config/entitlements';
import { resolveRuntimeTier } from '../utils/runtimeTier';
import { DEBUG_ENABLED } from '../utils/DebugLogger';

const debugLog = (...args) => {
  if (DEBUG_ENABLED) {
    console.debug(...args);
  }
};

/**
 * SessionManager Service - Battle Session Storage and Management
 *
 * This service handles saving, loading, and managing battle sessions using
 * file-based storage for production use. It provides a unified interface for
 * session persistence that works across the entire application.
 *
 * KEY FEATURES:
 * - File-Based Storage: Sessions saved to production directory for persistence
 * - Dashboard Integration: Saved sessions appear on dashboard for easy access
 * - Session Metadata: Tracks creation date, last modified, player count, etc.
 * - Auto-naming: Generates meaningful session names with timestamps
 * - Validation: Ensures session data integrity before saving
 *
 * STORAGE FORMAT:
 * - Production Directory: C:\Users\sexsm\Ascendant\Saved Sessions\ (TEMPORARY - will be S3 or similar cloud storage)
 * - File Format: JSON files with session data
 * - Metadata Index: sessions_index.json tracks all session metadata
 * - Each session: { id, name, fileName, metadata, createdAt, lastModified }
 *
 * INTEGRATION:
 * - Used by BattleRoom for save/load operations
 * - Used by Dashboard to display available sessions
 * - Provides both programmatic and user-friendly interfaces
 *
 * @date_of_change 2025-09-16 - Implemented production file-based storage
 */

class SessionManager {
  constructor() {
    // @date_of_change 2025-09-16 - PRODUCTION FILE-BASED STORAGE
    // TEMPORARY: Using local file system, will be replaced with S3 or similar cloud storage
    this.sessionsDirectory = 'C:\\Users\\sexsm\\Ascendant\\Saved Sessions\\';
    this.indexFileName = 'sessions_index.json';
    this.maxSessions = 100; // Increased limit for file-based storage
    this.fallbackStorageKey = SESSION_INDEX_STORAGE_KEY; // Fallback for metadata only
  }
  
  /**
   * Optimize session data by extracting large character data to prevent localStorage overflow
   * @date_of_change 2025-09-06 - LOCAL STORAGE OVERFLOW FIX
   * @param {Object} data - Session data to optimize
   * @returns {Object} - Optimized copy with character references instead of full data
   */
  optimizeSessionDataForStorage(data) {
    debugLog('[SessionManager.optimizeSessionDataForStorage] Starting data optimization');
    
    if (!data?.battleState?.tokens) {
      debugLog('[SessionManager.optimizeSessionDataForStorage] No tokens to optimize');
      return data;
    }

    const serializedCollection = serializeBattleRoomTokenCollection(data.battleState.tokens);
    const characterTemplates = {
      ...(data.characterTemplates && typeof data.characterTemplates === 'object' ? data.characterTemplates : {}),
      ...serializedCollection.templateMap
    };
    
    // Create optimized session data
    const optimizedData = {
      ...data,
      battleState: {
        ...data.battleState,
        tokens: serializedCollection.tokens
      },
      // Store character templates separately
      characterTemplates
    };

    const estimatedSizeBefore = JSON.stringify(data).length;
    const estimatedSizeAfter = JSON.stringify(optimizedData).length;
    
    debugLog(`[SessionManager.optimizeSessionDataForStorage] Optimization complete:`, {
      originalTokens: data.battleState.tokens.length,
      optimizedTokens: serializedCollection.tokens.length,
      extractedTemplates: Object.keys(characterTemplates).length,
      estimatedSizeBefore,
      estimatedSizeAfter,
      compressionRatio: estimatedSizeBefore > 0
        ? Math.round((1 - estimatedSizeAfter / estimatedSizeBefore) * 100) + '%'
        : '0%'
    });
    
    return optimizedData;
  }

  /**
   * Create a safe filename for file storage
   * @date_of_change 2025-09-16 - PRODUCTION FILE-BASED STORAGE
   * @param {string} sessionName - Original session name
   * @param {string} sessionId - Session ID
   * @returns {string} - Safe filename for file system
   */
  createSafeFileName(sessionName, sessionId) {
    // Remove invalid filename characters and limit length
    const safeName = sessionName
      .replace(/[<>:"/\\|?*]/g, '') // Remove invalid characters
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .substring(0, 50); // Limit length

    return `${safeName}_${sessionId}.json`;
  }

  /**
   * Save session file to production directory using browser download API
   * @date_of_change 2025-09-16 - PRODUCTION FILE-BASED STORAGE
   * TEMPORARY: Using browser download, will be replaced with backend API for S3 storage
   * @param {string} fileName - Name of the file to save
   * @param {Object} sessionData - Session data to save
   * @returns {Promise<boolean>} - Success status
   */
  async saveSessionFile(fileName, sessionData) {
    try {
      debugLog(`[SessionManager.saveSessionFile] Saving file: ${fileName}`);

      // Create the data string
      const dataStr = JSON.stringify(sessionData, null, 2);

      // Create a blob and download link
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      // Create download link
      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = fileName;
      downloadLink.style.display = 'none';

      // Trigger download
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      // Clean up
      URL.revokeObjectURL(url);

      debugLog(`[SessionManager.saveSessionFile] Successfully saved: ${fileName}`);
      return true;

    } catch (error) {
      console.error(`[SessionManager.saveSessionFile] Failed to save file ${fileName}:`, error);
      return false;
    }
  }

  /**
   * Load session file from production directory using file input
   * @date_of_change 2025-09-16 - PRODUCTION FILE-BASED STORAGE
   * TEMPORARY: Using file input dialog, will be replaced with backend API for S3 storage
   * @param {string} fileName - Name of the file to load
   * @returns {Promise<Object>} - Session data
   */
  loadSessionFile(fileName) {
    return new Promise((resolve, reject) => {
      debugLog(`[SessionManager.loadSessionFile] Loading file: ${fileName}`);

      // Create file input
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.json';
      fileInput.style.display = 'none';

      fileInput.onchange = (event) => {
        const file = event.target.files[0];
        if (!file) {
          debugLog(`[SessionManager.loadSessionFile] No file selected for ${fileName}`);
          resolve(null);
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const sessionData = JSON.parse(e.target.result);
            debugLog(`[SessionManager.loadSessionFile] Successfully loaded: ${file.name}`);
            resolve(sessionData);
          } catch (parseError) {
            console.error(`[SessionManager.loadSessionFile] Failed to parse ${file.name}:`, parseError);
            reject(new Error(`Invalid session file: ${parseError.message}`));
          }
        };

        reader.onerror = () => {
          console.error(`[SessionManager.loadSessionFile] Failed to read file ${file.name}`);
          reject(new Error('Failed to read session file'));
        };

        reader.readAsText(file);
      };

      // Trigger file selection
      document.body.appendChild(fileInput);
      fileInput.click();
      document.body.removeChild(fileInput);
    });
  }

  /**
   * Load session index from production directory or localStorage fallback
   * @date_of_change 2025-09-16 - PRODUCTION FILE-BASED STORAGE
   * @returns {Array} - Array of session metadata
   */
  loadSessionIndex() {
    try {
      // TEMPORARY: Using localStorage until backend file API is implemented
      // In production, this will load from the production directory index file
      const indexJson = localStorage.getItem(this.fallbackStorageKey);
      if (!indexJson) return [];

      return JSON.parse(indexJson);

    } catch (error) {
      console.error('[SessionManager.loadSessionIndex] Failed to load session index:', error);
      return [];
    }
  }

  /**
   * Save session index to production directory or localStorage fallback
   * @date_of_change 2025-09-16 - PRODUCTION FILE-BASED STORAGE
   * @param {Array} sessions - Array of session metadata
   * @returns {Promise<boolean>} - Success status
   */
  async saveSessionIndex(sessions) {
    try {
      // TEMPORARY: Using localStorage until backend file API is implemented
      // In production, this will save to the production directory index file
      localStorage.setItem(this.fallbackStorageKey, JSON.stringify(sessions));
      debugLog(`[SessionManager.saveSessionIndex] Index saved with ${sessions.length} sessions`);
      return true;

    } catch (error) {
      console.error('[SessionManager.saveSessionIndex] Failed to save session index:', error);
      return false;
    }
  }

  /**
   * Update session index with new or modified session metadata
   * @date_of_change 2025-09-16 - PRODUCTION FILE-BASED STORAGE
   * @param {Object} sessionMetadata - Session metadata to add/update
   * @param {boolean} overwrite - Whether to overwrite existing sessions
   * @returns {Promise<boolean>} - Success status
   */
  async updateSessionIndex(sessionMetadata, overwrite = false) {
    try {
      const sessions = this.getAllSessions();
      const existingIndex = sessions.findIndex(s => s.id === sessionMetadata.id);

      if (existingIndex !== -1) {
        // Update existing session
        sessions[existingIndex] = sessionMetadata;
      } else {
        // Add new session to the beginning
        sessions.unshift(sessionMetadata);
      }

      // Limit total sessions
      if (sessions.length > this.maxSessions) {
        sessions.splice(this.maxSessions);
        debugLog(`[SessionManager.updateSessionIndex] Limited sessions to ${this.maxSessions} max`);
      }

      return await this.saveSessionIndex(sessions);

    } catch (error) {
      console.error('[SessionManager.updateSessionIndex] Failed to update session index:', error);
      return false;
    }
  }

  /**
   * Restore character data from references when loading session
   * @date_of_change 2025-09-06 - LOCAL STORAGE OVERFLOW FIX
   * @param {Object} sessionData - Session data with character references
   * @returns {Object} - Session data with restored character data
   */
  restoreCharacterDataFromReferences(sessionData) {
    debugLog('[SessionManager.restoreCharacterDataFromReferences] Restoring character data');
    
    if (!sessionData?.battleState?.tokens) {
      debugLog('[SessionManager.restoreCharacterDataFromReferences] No character references to restore');
      return sessionData;
    }

    const templateMap = sessionData.characterTemplates && typeof sessionData.characterTemplates === 'object'
      ? sessionData.characterTemplates
      : {};
    // date_of_change: 2026-05-19 - GAP-001: Supply real local entity maps so the rehydrator can
    // fall back to canonical character/monster data when the embedded template is absent or sparse.
    // rule_reference: BattleRoomSnapshotRehydrator restoreSnapshotToken — templateLookupCandidates
    // are checked against canonicalCharacterEntities / canonicalMonsterEntities.
    const canonicalCharacterEntities = UnifiedDataManager.getAllFromLocalStorage
      ? (UnifiedDataManager.getAllFromLocalStorage('character') || {})
      : {};
    const canonicalMonsterEntities = UnifiedDataManager.getAllFromLocalStorage
      ? (UnifiedDataManager.getAllFromLocalStorage('monster') || {})
      : {};
    const restoredTokens = restoreSnapshotTokenCollection({
      tokens: sessionData.battleState.tokens,
      templateMap,
      canonicalCharacterEntities,
      canonicalMonsterEntities
    });
    
    // Return session with restored tokens, removing the template storage
    const restoredSessionData = {
      ...sessionData,
      battleState: {
        ...sessionData.battleState,
        tokens: restoredTokens
      },
      characterTemplates: undefined // Remove templates after restoration
    };
    
    debugLog('[SessionManager.restoreCharacterDataFromReferences] Character data restoration complete');
    return restoredSessionData;
  }

  /**
   * Sanitizes data to prevent circular references before serialization
   * @param {Object} data - Data to sanitize
   * @returns {Object} - Sanitized copy of the data safe for JSON serialization
   */
  sanitizeDataForSerialization(data) {
    debugLog('[SessionManager.sanitize] Starting data sanitization');
    
    // Handle null/undefined case
    if (data === null || data === undefined) {
      return data;
    }
    
    // Use a Set to keep track of processed objects to avoid circular references
    const seen = new Set();
    
    // Process object and remove circular references
    const sanitize = (obj, path = '') => {
      // Handle primitive types (these are always safe)
      if (obj === null || obj === undefined || typeof obj !== 'object') {
        return obj;
      }
      
      // Skip DOM nodes and functions which aren't serializable
      if (obj instanceof Node || typeof obj === 'function') {
        debugLog(`[SessionManager.sanitize] Skipping non-serializable at ${path}:`, typeof obj);
        return null;
      }
      
      // Handle special objects like Date
      if (obj instanceof Date) {
        return obj.toISOString();
      }
      
      // Handle arrays
      if (Array.isArray(obj)) {
        return obj.map((item, index) => sanitize(item, `${path}[${index}]`));
      }
      
      // Check for circular reference using object identity
      const objId = obj.id || obj.name || JSON.stringify(Object.keys(obj).slice(0, 2));
      const objPath = `${path}/${objId}`;
      
      if (seen.has(obj)) {
        debugLog(`[SessionManager.sanitize] Circular reference detected at ${objPath}`);
        return null; // or a safe representation
      }
      
      // Mark this object as seen
      seen.add(obj);
      
      // Process object properties
      const result = {};
      for (const [key, value] of Object.entries(obj)) {
        // Skip known problematic properties
        if (
          key === 'parent' || 
          key === 'owner' || 
          key === 'refs' ||
          key === '_owner' ||
          key === 'stageRef' ||
          key === 'layerRef'
        ) {
          debugLog(`[SessionManager.sanitize] Skipping problematic property: ${objPath}.${key}`);
          continue;
        }
        
        // Recursively sanitize value
        result[key] = sanitize(value, `${objPath}.${key}`);
      }
      
      // Clean up
      seen.delete(obj);
      
      return result;
    };
    
    // Start sanitization process
    const result = sanitize(data, 'root');
    debugLog('[SessionManager.sanitize] Data sanitization complete');
    return result;
  }

  /**
   * Save a battle session to production file storage
   * @date_of_change 2025-09-16 - PRODUCTION FILE-BASED STORAGE
   * @param {Object} sessionData - Complete battle state from useBattleRoom
   * @param {string} customName - Optional custom name for the session
   * @param {boolean} overwrite - Whether to overwrite an existing session
   * @param {string} sessionIdToOverwrite - Optional specific session ID to overwrite
   * @returns {Object} - Saved session object with metadata
   */
  async saveSession(sessionData, customName = null, overwrite = false, sessionIdToOverwrite = null) {
    debugLog('[SessionManager.saveSession] Starting production file-based save process');
    try {
      const userTier = resolveRuntimeTier();
      const tierConfig = getTierConfig(userTier);
      const existingSessions = this.getAllSessions();
      const sessionLimit = tierConfig.limits.savedSessions;
      const overwriteTargetId = sessionIdToOverwrite
        || (overwrite && existingSessions.length > 0 ? existingSessions[0].id : null);

      if (sessionLimit !== 'unlimited' && !overwriteTargetId && existingSessions.length >= sessionLimit) {
        throw new Error('Free captains get 1 saved battle. Overwrite your current save or upgrade to Game Master for a full campaign archive.');
      }

      // Debug the incoming data structure
      debugLog('[SessionManager.saveSession] Session data structure:', {
        version: sessionData.version,
        hasTokens: !!sessionData.battleState?.tokens,
        tokenCount: sessionData.battleState?.tokens?.length || 0,
        hasMapImage: !!sessionData.battleState?.mapImage,
        hasMapImages: !!sessionData.battleState?.mapImages,
        hasLayers: !!sessionData.battleState?.layers
      });

      // Sanitize the data to prevent circular references
      debugLog('[SessionManager.saveSession] Sanitizing session data for file storage');
      const sanitizedData = this.sanitizeDataForSerialization(sessionData);
      debugLog('[SessionManager.saveSession] Session data sanitized successfully');
      const optimizedData = this.optimizeSessionDataForStorage(sanitizedData);
      debugLog('[SessionManager.saveSession] Session data optimized to snapshot contract successfully');

      // Generate session metadata
      const now = new Date();
      const previousSession = overwriteTargetId
        ? existingSessions.find((session) => session.id === overwriteTargetId) || null
        : null;
      const sessionId = overwriteTargetId || this.generateSessionId();
      const sessionName = customName || this.generateSessionName(sessionData, now);
      const expiresAt = tierConfig.limits.sessionExpirationDays == null
        ? null
        : new Date(now.getTime() + tierConfig.limits.sessionExpirationDays * 24 * 60 * 60 * 1000).toISOString();

      // Create safe filename (remove invalid characters)
      const safeFileName = this.createSafeFileName(sessionName, sessionId);
      debugLog(`[SessionManager.saveSession] Generated session: ${sessionId}, name: ${sessionName}, file: ${safeFileName}`);

      // Create complete session file data
      const sessionFileData = {
        id: sessionId,
        name: sessionName,
        data: optimizedData,
        metadata: {
          tokenCount: optimizedData.battleState?.tokens?.length || 0,
          hasMap: !!(optimizedData.battleState?.mapImage || optimizedData.battleState?.mapImages),
          gridSize: optimizedData.battleState?.gridSize || 50,
          zoom: optimizedData.battleState?.zoom || 1,
          activeLayer: optimizedData.battleState?.activeLayer || 'token',
          version: '2.2' // Snapshot contract adoption
        },
        createdAt: previousSession?.createdAt || now.toISOString(),
        expiresAt,
        lastModified: now.toISOString()
      };

      // Save session file to production directory
      debugLog(`[SessionManager.saveSession] Saving session file to production directory`);
      const success = await this.saveSessionFile(safeFileName, sessionFileData);

      if (!success) {
        throw new Error('Failed to save session file to production directory');
      }

      // @date_of_change 2025-09-17 - SEAMLESS LOADING: Store full data for reasonable-sized sessions
      // Check session data size for seamless loading capability
      const sessionDataStr = JSON.stringify(optimizedData);
      const sessionDataSize = sessionDataStr.length;
      const maxLocalStorageSize = 1024 * 1024; // 1MB limit for localStorage sessions
      const shouldStoreData = sessionDataSize < maxLocalStorageSize;

      console.log(`[SessionManager.saveSession] Session data size: ${(sessionDataSize / 1024).toFixed(1)}KB, embed in localStorage: ${shouldStoreData}`);

      // Create session metadata for index
      const sessionMetadata = {
        id: sessionId,
        name: sessionName,
        fileName: safeFileName,
        filePath: `${this.sessionsDirectory}${safeFileName}`,
        // @date_of_change 2025-09-17 - EMBED DATA: Store full data for seamless loading when size permits
        data: shouldStoreData ? optimizedData : null, // Embed data for small sessions
        dataSize: sessionDataSize,
        hasEmbeddedData: shouldStoreData,
        metadata: {
          tokenCount: optimizedData.battleState?.tokens?.length || 0,
          hasMap: !!(optimizedData.battleState?.mapImage || optimizedData.battleState?.mapImages),
          gridSize: optimizedData.battleState?.gridSize || 50,
          zoom: optimizedData.battleState?.zoom || 1,
          activeLayer: optimizedData.battleState?.activeLayer || 'token',
          version: '2.2'
        },
        createdAt: previousSession?.createdAt || now.toISOString(),
        expiresAt,
        lastModified: now.toISOString()
      };

      // Update session index
      await this.updateSessionIndex(sessionMetadata, overwrite);

      console.log(`[SessionManager.saveSession] Session saved successfully: ${sessionName} (${sessionId})`);
      return sessionMetadata;

    } catch (error) {
      console.error('[SessionManager.saveSession] Failed to save session:', error);
      console.error('[SessionManager.saveSession] Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });

      // Throw user-friendly error message
      throw new Error(error.message || 'Failed to save battle session to production storage.');
    }
  }

  /**
   * Load a battle session by ID
   * @param {string} sessionId - ID of the session to load
   * @returns {Object|null} - Session data or null if not found
   */
  /**
   * Load session data from production file storage
   * @date_of_change 2025-09-16 - PRODUCTION FILE-BASED STORAGE
   * @param {string} sessionId - Session ID to load
   * @returns {Promise} - Promise that resolves with session data from production storage
   */
  async loadSession(sessionId) {
    try {
      console.log(`[SessionManager.loadSession] 🔍 DEBUGGING: Starting load for session ID: ${sessionId}`);
      const sessions = this.getAllSessions();
      console.log(`[SessionManager.loadSession] 🔍 DEBUGGING: Found ${sessions.length} total sessions in index`);
      const sessionMetadata = sessions.find(s => s.id === sessionId);

      if (!sessionMetadata) {
        console.warn(`[SessionManager.loadSession] Session metadata not found: ${sessionId}`);
        console.log(`[SessionManager.loadSession] 🔍 DEBUGGING: Available session IDs:`, sessions.map(s => s.id));
        return null;
      }

      if (isSessionExpired(sessionMetadata)) {
        const expiredError = new Error(`"${sessionMetadata.name}" has expired. Clear it out or upgrade to Game Master to keep long campaigns alive.`);
        expiredError.code = 'SESSION_EXPIRED';
        expiredError.session = sessionMetadata;
        throw expiredError;
      }

      console.log(`[SessionManager.loadSession] Found session metadata: ${sessionMetadata.name} (${sessionId})`);

      // @date_of_change 2025-09-17 - PRIORITY: Check for embedded data first to avoid file dialogs
      if (sessionMetadata.data && Object.keys(sessionMetadata.data).length > 0) {
        console.log(`[SessionManager.loadSession] ✅ Using embedded data from localStorage (${(JSON.stringify(sessionMetadata.data).length / 1024).toFixed(1)}KB)`);
        return sessionMetadata.data;
      }

      // @date_of_change 2025-09-17 - COMPATIBILITY: Check if data is stored at session level
      if (sessionMetadata.battleState || sessionMetadata.version) {
        console.log(`[SessionManager.loadSession] ✅ Using direct session data from localStorage`);
        return sessionMetadata;
      }

      // @date_of_change 2025-09-17 - LEGACY COMPATIBILITY: Check for old storage format
      console.log(`[SessionManager.loadSession] 🔍 Checking for legacy session data storage...`);
      const legacyData = this.checkLegacySessionData(sessionId, sessionMetadata);
      if (legacyData) {
        console.log(`[SessionManager.loadSession] ✅ Found legacy session data`);
        return legacyData;
      }

      console.log(`[SessionManager.loadSession] ⚠️ No embedded data found - would require file selection for: ${sessionMetadata.fileName}`);
      // Don't trigger file dialog automatically - let the UI handle this
      throw new Error(`Session "${sessionMetadata.name}" requires manual file selection`);

    } catch (error) {
      console.error('[SessionManager.loadSession] Failed to load session:', error);
      throw error;
    }
  }

  /**
   * Get all saved sessions for dashboard display
   * @date_of_change 2025-09-16 - PRODUCTION FILE-BASED STORAGE
   * @returns {Array} - Array of session metadata sorted by last modified
   */
  getAllSessions() {
    try {
      // Try to load session index from production directory first
      const indexData = this.loadSessionIndex();
      if (indexData && indexData.length > 0) {
        // Sort by last modified (newest first)
        return indexData
          .map((session) => ({
            ...session,
            isExpired: isSessionExpired(session)
          }))
          .sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified));
      }

      // Fallback to localStorage for backwards compatibility
      const sessionsJson = localStorage.getItem(this.fallbackStorageKey);
      if (!sessionsJson) return [];

      const sessions = JSON.parse(sessionsJson);

      // Sort by last modified (newest first)
      return sessions
        .map((session) => ({
          ...session,
          isExpired: isSessionExpired(session)
        }))
        .sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified));

    } catch (error) {
      console.error('[SessionManager] Failed to get sessions:', error);
      return [];
    }
  }

  /**
   * Check for legacy session data storage formats
   * @date_of_change 2025-09-17 - LEGACY COMPATIBILITY
   * @param {string} sessionId - Session ID to check
   * @param {Object} sessionMetadata - Session metadata
   * @returns {Object|null} - Legacy session data or null
   */
  checkLegacySessionData(sessionId, sessionMetadata) {
    try {
      // Check if session has full data stored under different storage key patterns
      const possibleKeys = [
        `ascendant_session_${sessionId}`,
        `session_${sessionId}`,
        `battleroom_session_${sessionId}`,
        sessionId // Direct storage under session ID
      ];

      for (const key of possibleKeys) {
        const storedData = localStorage.getItem(key);
        if (storedData) {
          try {
            const parsedData = JSON.parse(storedData);
            if (parsedData.battleState || parsedData.data?.battleState) {
              console.log(`[SessionManager.checkLegacySessionData] Found legacy data under key: ${key}`);
              return parsedData.data || parsedData;
            }
          } catch (parseError) {
            console.warn(`[SessionManager.checkLegacySessionData] Failed to parse data for key ${key}:`, parseError);
          }
        }
      }

      // Check if the session was stored in the old single-session format
      const oldSessionKey = this.fallbackStorageKey;
      const allSessionsData = localStorage.getItem(oldSessionKey);
      if (allSessionsData) {
        try {
          const sessions = JSON.parse(allSessionsData);
          // Look for session that has embedded battleState data
          const sessionWithData = sessions.find(s =>
            s.id === sessionId && (s.battleState || s.data?.battleState)
          );
          if (sessionWithData) {
            console.log(`[SessionManager.checkLegacySessionData] Found session with embedded data in sessions array`);
            return sessionWithData.data || sessionWithData;
          }
        } catch (parseError) {
          console.warn(`[SessionManager.checkLegacySessionData] Failed to parse sessions array:`, parseError);
        }
      }

      return null;
    } catch (error) {
      console.error('[SessionManager.checkLegacySessionData] Error checking legacy data:', error);
      return null;
    }
  }

  /**
   * Delete a session by ID
   * @param {string} sessionId - ID of the session to delete
   * @returns {boolean} - Success status
   */
  deleteSession(sessionId) {
    try {
      const sessions = this.getAllSessions();
      const filteredSessions = sessions.filter(s => s.id !== sessionId);
      
      localStorage.setItem(this.fallbackStorageKey, JSON.stringify(filteredSessions));
      
      console.log(`[SessionManager] Session deleted: ${sessionId}`);
      return true;
      
    } catch (error) {
      console.error('[SessionManager] Failed to delete session:', error);
      return false;
    }
  }

  /**
   * Update an existing session
   * @param {Object} updatedSession - Session object with updates
   * @returns {boolean} - Success status
   */
  /**
   * Update an existing session metadata
   * @date_of_change 2025-09-16 - PRODUCTION FILE-BASED STORAGE
   * @param {Object} updatedSession - Session metadata with updates
   * @param {boolean} forceUpdate - Force update even if session doesn't exist (create new)
   * @returns {Promise<boolean>} - Success status
   */
  async updateSession(updatedSession, forceUpdate = false) {
    try {
      const sessions = this.getAllSessions();
      const sessionIndex = sessions.findIndex(s => s.id === updatedSession.id);

      if (sessionIndex !== -1) {
        // Update existing session metadata
        sessions[sessionIndex] = {
          ...sessions[sessionIndex],
          ...updatedSession,
          lastModified: new Date().toISOString()
        };

        return await this.saveSessionIndex(sessions);
      } else if (forceUpdate) {
        // Create new session if it doesn't exist but forceUpdate is true
        const newSession = {
          ...updatedSession,
          createdAt: updatedSession.createdAt || new Date().toISOString(),
          lastModified: new Date().toISOString()
        };

        const updatedSessions = [newSession, ...sessions];
        return await this.saveSessionIndex(updatedSessions);
      }

      return false;

    } catch (error) {
      console.error('[SessionManager] Failed to update session:', error);
      return false;
    }
  }

  /**
   * Download a session as a JSON file (legacy support)
   * @param {Object} session - Session object to download
   */
  downloadSession(session) {
    console.log('[SessionManager.downloadSession] Starting download process for session:', session.name);
    try {
      if (!session || !session.data) {
        console.error('[SessionManager.downloadSession] Invalid session object:', session);
        throw new Error('Invalid session data for download');
      }

      // Create a downloadable JSON file
      console.log('[SessionManager.downloadSession] Serializing session data for download');
      const optimizedData = this.optimizeSessionDataForStorage(session.data);
      const dataStr = JSON.stringify(optimizedData, null, 2);
      console.log(`[SessionManager.downloadSession] Serialized ${dataStr.length} characters`);
      
      const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;
      console.log('[SessionManager.downloadSession] Created data URI for download');
      
      // Create a download link and trigger it
      const downloadLink = document.createElement('a');
      downloadLink.setAttribute('href', dataUri);
      downloadLink.setAttribute('download', `${session.name}.json`);
      document.body.appendChild(downloadLink);
      
      console.log(`[SessionManager.downloadSession] Triggering download for ${session.name}.json`);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
      console.log('[SessionManager.downloadSession] Download process completed successfully');
      return true;
    } catch (error) {
      console.error('[SessionManager.downloadSession] Failed to download session:', error);
      console.error('[SessionManager.downloadSession] Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      return false;
    }
  }
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Generate a meaningful session name based on content and timestamp
   * @param {Object} sessionData - Battle session data
   * @param {Date} timestamp - Creation timestamp
   * @returns {string} - Generated session name
   */
  generateSessionName(sessionData, timestamp) {
    const battleState = sessionData.battleState || {};
    const tokens = battleState.tokens || [];
    const tokenCount = tokens.length;
    
    // Try to create a meaningful name based on content
    let baseName = 'Battle Room';
    
    if (tokenCount > 0) {
      const characterCount = tokens.filter(t => t.type === 'player').length;
      const monsterCount = tokens.filter(t => t.type === 'monster').length;
      
      if (characterCount > 0 && monsterCount > 0) {
        baseName = `${characterCount} Heroes vs ${monsterCount} Monsters`;
      } else if (characterCount > 0) {
        baseName = `${characterCount} Character${characterCount > 1 ? 's' : ''} Room`;
      } else if (monsterCount > 0) {
        baseName = `${monsterCount} Monster${monsterCount > 1 ? 's' : ''} Encounter`;
      }
    }
    
    // Add timestamp
    const timeStr = timestamp.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    
    return `${baseName} - ${timeStr}`;
  }

  /**
   * Get session statistics for dashboard display
   * @returns {Object} - Statistics about saved sessions
   */
  getSessionStats() {
    const sessions = this.getAllSessions();
    
    return {
      totalSessions: sessions.length,
      totalTokens: sessions.reduce((sum, s) => sum + (s.metadata.tokenCount || 0), 0),
      sessionsWithMaps: sessions.filter(s => s.metadata.hasMap).length,
      recentSessions: sessions.slice(0, 5), // Last 5 sessions
      oldestSession: sessions.length > 0 ? sessions[sessions.length - 1] : null,
      newestSession: sessions.length > 0 ? sessions[0] : null
    };
  }

  /**
   * Clear all sessions (with confirmation)
   * @returns {boolean} - Success status
   */
  clearAllSessions() {
    try {
      localStorage.removeItem(this.fallbackStorageKey);
      console.log('[SessionManager] All sessions cleared');
      return true;
    } catch (error) {
      console.error('[SessionManager] Failed to clear sessions:', error);
      return false;
    }
  }
}

// Export singleton instance
const sessionManager = new SessionManager();
export default sessionManager;
